import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import {
  Upload,
  FileUp,
  CheckCircle,
  XCircle,
  Loader,
  AlertCircle,
  Download,
  FileJson,
  FileSpreadsheet,
  Trash2,
  Plus
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const BulkUpload = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState('');
  const [episodes, setEpisodes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState([]);
  const [uploadMode, setUploadMode] = useState('manual'); // 'manual' or 'csv'

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    if (user?.role !== 'artist' && user?.role !== 'author') {
      toast.error('Creator access only');
      navigate('/');
      return;
    }
    fetchUserSeries();
  }, [isAuthenticated, user]);

  const fetchUserSeries = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/series/user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSeries(response.data);
    } catch (error) {
      console.error('Error fetching series:', error);
      toast.error('Failed to load your series');
    }
  };

  const handleAddEpisode = () => {
    const newEpisode = {
      id: Date.now(),
      title: '',
      description: '',
      chapterNumber: episodes.length + 1,
      images: [],
      status: 'pending'
    };
    setEpisodes([...episodes, newEpisode]);
  };

  const handleRemoveEpisode = (id) => {
    setEpisodes(episodes.filter(ep => ep.id !== id));
  };

  const handleEpisodeChange = (id, field, value) => {
    setEpisodes(episodes.map(ep =>
      ep.id === id ? { ...ep, [field]: value } : ep
    ));
  };

  const handleImagesUpload = (id, files) => {
    const fileArray = Array.from(files);
    setEpisodes(episodes.map(ep =>
      ep.id === id ? { ...ep, images: fileArray } : ep
    ));
  };

  const handleCSVImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        
        const imported = lines.slice(1).filter(line => line.trim()).map((line, index) => {
          const values = line.split(',').map(v => v.trim());
          return {
            id: Date.now() + index,
            title: values[0] || '',
            description: values[1] || '',
            chapterNumber: parseInt(values[2]) || index + 1,
            images: [],
            status: 'pending'
          };
        });

        setEpisodes(imported);
        toast.success(`Imported ${imported.length} episodes from CSV`);
      } catch (error) {
        toast.error('Failed to parse CSV file');
      }
    };
    reader.readAsText(file);
  };

  const handleJSONImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        const imported = json.map((item, index) => ({
          id: Date.now() + index,
          title: item.title || '',
          description: item.description || '',
          chapterNumber: item.chapterNumber || index + 1,
          images: [],
          status: 'pending'
        }));

        setEpisodes(imported);
        toast.success(`Imported ${imported.length} episodes from JSON`);
      } catch (error) {
        toast.error('Failed to parse JSON file');
      }
    };
    reader.readAsText(file);
  };

  const downloadCSVTemplate = () => {
    const csv = 'Title,Description,Chapter Number\n"Episode 1 Title","Episode description here",1\n"Episode 2 Title","Another description",2';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'episode_template.csv';
    a.click();
  };

  const downloadJSONTemplate = () => {
    const template = [
      { title: "Episode 1 Title", description: "Episode description here", chapterNumber: 1 },
      { title: "Episode 2 Title", description: "Another description", chapterNumber: 2 }
    ];
    const json = JSON.stringify(template, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'episode_template.json';
    a.click();
  };

  const uploadEpisode = async (episode, index) => {
    try {
      const token = localStorage.getItem('token');
      
      // Upload images first
      const imageUrls = [];
      for (let i = 0; i < episode.images.length; i++) {
        const formData = new FormData();
        formData.append('image', episode.images[i]);

        const uploadResponse = await axios.post(`${API}/upload/episode_panel`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        imageUrls.push(uploadResponse.data.url);
      }

      // Create episode
      const episodeData = {
        series_id: selectedSeries,
        title: episode.title,
        description: episode.description,
        chapter_number: episode.chapterNumber,
        images: imageUrls
      };

      await axios.post(`${API}/chapters`, episodeData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setProgress(prev => prev.map((item, i) =>
        i === index ? { ...item, status: 'success', message: 'Uploaded successfully' } : item
      ));

      return true;
    } catch (error) {
      console.error('Error uploading episode:', error);
      setProgress(prev => prev.map((item, i) =>
        i === index ? { ...item, status: 'error', message: error.response?.data?.detail || 'Upload failed' } : item
      ));
      return false;
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedSeries) {
      toast.error('Please select a series');
      return;
    }

    if (episodes.length === 0) {
      toast.error('Please add at least one episode');
      return;
    }

    // Validate all episodes have required fields
    const invalid = episodes.filter(ep => !ep.title || !ep.description || ep.images.length === 0);
    if (invalid.length > 0) {
      toast.error(`${invalid.length} episode(s) missing required fields`);
      return;
    }

    setUploading(true);
    setProgress(episodes.map((ep, i) => ({
      id: ep.id,
      title: ep.title,
      status: 'uploading',
      message: 'Uploading...'
    })));

    let successCount = 0;
    for (let i = 0; i < episodes.length; i++) {
      const success = await uploadEpisode(episodes[i], i);
      if (success) successCount++;
      // Small delay between uploads to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setUploading(false);
    
    if (successCount === episodes.length) {
      toast.success(`All ${successCount} episodes uploaded successfully!`);
      setTimeout(() => navigate(`/series/${selectedSeries}`), 2000);
    } else {
      toast.warning(`${successCount} of ${episodes.length} episodes uploaded successfully`);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white py-8">
      <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Bulk Episode Upload</h1>
          <p className="text-gray-400">Upload multiple episodes at once to save time</p>
        </div>

        {/* Mode Selection */}
        <Card className="bg-gray-900 border-gray-800 mb-6">
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-4">
              <Button
                onClick={() => setUploadMode('manual')}
                variant={uploadMode === 'manual' ? 'default' : 'outline'}
                className={uploadMode === 'manual' ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-700'}
              >
                <Plus className="w-4 h-4 mr-2" />
                Manual Entry
              </Button>
              <Button
                onClick={() => setUploadMode('csv')}
                variant={uploadMode === 'csv' ? 'default' : 'outline'}
                className={uploadMode === 'csv' ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-700'}
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
              <Button
                onClick={() => setUploadMode('json')}
                variant={uploadMode === 'json' ? 'default' : 'outline'}
                className={uploadMode === 'json' ? 'bg-orange-500 hover:bg-orange-600' : 'border-gray-700'}
              >
                <FileJson className="w-4 h-4 mr-2" />
                Import JSON
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Import Templates */}
        {(uploadMode === 'csv' || uploadMode === 'json') && (
          <Card className="bg-gray-900 border-gray-800 mb-6">
            <CardHeader className="border-b border-gray-800">
              <h2 className="text-xl font-bold">Import {uploadMode.toUpperCase()}</h2>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <p className="text-gray-400 mb-4">
                    Download a template file, fill it with your episode data, then upload it here.
                  </p>
                  <div className="flex gap-3">
                    <Button
                      onClick={uploadMode === 'csv' ? downloadCSVTemplate : downloadJSONTemplate}
                      variant="outline"
                      className="border-gray-700"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </Button>
                    
                    <label className="cursor-pointer">
                      <Button variant="outline" className="border-gray-700" as="span">
                        <Upload className="w-4 h-4 mr-2" />
                        Upload {uploadMode.toUpperCase()}
                      </Button>
                      <input
                        type="file"
                        accept={uploadMode === 'csv' ? '.csv' : '.json'}
                        onChange={uploadMode === 'csv' ? handleCSVImport : handleJSONImport}
                        className="sr-only"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Series Selection */}
        <Card className="bg-gray-900 border-gray-800 mb-6">
          <CardHeader className="border-b border-gray-800">
            <h2 className="text-xl font-bold">Select Series</h2>
          </CardHeader>
          <CardContent className="p-6">
            <select
              value={selectedSeries}
              onChange={(e) => setSelectedSeries(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white"
            >
              <option value="">Choose a series...</option>
              {series.map(s => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
            </select>
          </CardContent>
        </Card>

        {/* Episodes List */}
        <Card className="bg-gray-900 border-gray-800 mb-6">
          <CardHeader className="border-b border-gray-800">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Episodes ({episodes.length})</h2>
              {uploadMode === 'manual' && (
                <Button onClick={handleAddEpisode} size="sm" className="bg-orange-500 hover:bg-orange-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Episode
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {episodes.length === 0 ? (
              <div className="text-center py-12">
                <FileUp className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No episodes added yet</p>
                <p className="text-sm text-gray-500 mt-2">Add episodes manually or import from CSV/JSON</p>
              </div>
            ) : (
              <div className="space-y-6">
                {episodes.map((episode, index) => (
                  <div key={episode.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-start justify-between mb-4">
                      <Badge className="bg-orange-500/10 text-orange-500">
                        Chapter {episode.chapterNumber}
                      </Badge>
                      <button
                        onClick={() => handleRemoveEpisode(episode.id)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Title *</label>
                        <input
                          type="text"
                          value={episode.title}
                          onChange={(e) => handleEpisodeChange(episode.id, 'title', e.target.value)}
                          className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                          placeholder="Episode title"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Description *</label>
                        <textarea
                          value={episode.description}
                          onChange={(e) => handleEpisodeChange(episode.id, 'description', e.target.value)}
                          className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white"
                          rows="3"
                          placeholder="Episode description"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2">Images * ({episode.images.length})</label>
                        <label className="cursor-pointer block">
                          <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 hover:border-orange-500 transition-colors">
                            <div className="text-center">
                              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                              <p className="text-sm text-gray-400">
                                {episode.images.length > 0
                                  ? `${episode.images.length} images selected`
                                  : 'Click to upload images'}
                              </p>
                            </div>
                          </div>
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => handleImagesUpload(episode.id, e.target.files)}
                            className="sr-only"
                          />
                        </label>
                      </div>

                      {/* Progress indicator */}
                      {progress[index] && (
                        <div className="flex items-center gap-2 mt-2">
                          {progress[index].status === 'uploading' && (
                            <>
                              <Loader className="w-4 h-4 animate-spin text-blue-500" />
                              <span className="text-sm text-blue-500">{progress[index].message}</span>
                            </>
                          )}
                          {progress[index].status === 'success' && (
                            <>
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              <span className="text-sm text-green-500">{progress[index].message}</span>
                            </>
                          )}
                          {progress[index].status === 'error' && (
                            <>
                              <XCircle className="w-4 h-4 text-red-500" />
                              <span className="text-sm text-red-500">{progress[index].message}</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Button */}
        {episodes.length > 0 && (
          <Card className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/20">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-bold text-lg mb-1">Ready to Upload</h3>
                  <p className="text-sm text-gray-400">
                    {episodes.length} episode(s) will be uploaded to the selected series
                  </p>
                </div>
                <Button
                  onClick={handleBulkUpload}
                  disabled={uploading || !selectedSeries}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 px-8"
                  size="lg"
                >
                  {uploading ? (
                    <>
                      <Loader className="w-5 h-5 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5 mr-2" />
                      Upload All Episodes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tips */}
        <Card className="bg-gray-900 border-gray-800 mt-6">
          <CardHeader className="border-b border-gray-800">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-500" />
              <h2 className="text-xl font-bold">Tips for Bulk Upload</h2>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <ul className="space-y-2 text-sm text-gray-400">
              <li>• <strong>Image naming:</strong> Name your images in order (e.g., 001.jpg, 002.jpg) for correct sequencing</li>
              <li>• <strong>File size:</strong> Keep images under 5MB each for faster uploads</li>
              <li>• <strong>Format:</strong> Use JPG or PNG format for best compatibility</li>
              <li>• <strong>CSV format:</strong> Use commas to separate fields, quotes for text with commas</li>
              <li>• <strong>Upload limit:</strong> Maximum 10 episodes per bulk upload (rate limiting)</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BulkUpload;
